import React, { useMemo } from 'react';
import { message, Modal } from 'antd';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import SprintHeader from './SprintHeader';
import SprintKpiCards from './SprintKpiCards';
import SprintTimeline from './SprintTimeline';
import SprintAIInsights from './SprintAIInsights';
import SprintProgressReport from './SprintProgressReport';
import SprintSettingsModal from './SprintSettingsModal';
import SprintReportModal from './SprintReportModal';
import useSprintState from './useSprintState';
import { computeSprintKpis, filterTasksToSprint, deriveSprintInsights, computeCompletionRate } from './sprintUtils';
import { TIMELINE_SECTIONS, ROW_HEIGHT, SECTION_ROW_HEIGHT } from '../timeline/constants';
import { groupTasksByStatus } from '../timeline/timelineUtils';
import { closeSprint } from '../api/projectManagementApi';

dayjs.extend(isBetween);

export default function SprintBoard({ tasks = [], loading = false, onTaskClick }) {
    const {
        sprints,
        sprintsLoading,
        selectedSprint,
        setSelectedSprint,
        refreshSelectedSprint,
        isSettingsOpen, setIsSettingsOpen,
        isReportOpen, setIsReportOpen,
        toggleSection,
        isSectionCollapsed,
    } = useSprintState();

    // ── Filter tasks to the selected sprint window ────────────────────────────
    const sprintTasks = useMemo(() => {
        if (!selectedSprint) return tasks;
        return filterTasksToSprint(tasks, selectedSprint.startDate, selectedSprint.endDate);
    }, [tasks, selectedSprint]);

    // ── Find the next sprint for KPI "Next Sprint" count ─────────────────────
    const nextSprint = useMemo(() => {
        if (!selectedSprint || !sprints.length) return null;
        return sprints
            .filter(s => new Date(s.startDate) > new Date(selectedSprint.endDate))
            .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))[0] || null;
    }, [sprints, selectedSprint]);

    // ── KPIs, insights, completion — all derived from filtered sprint tasks ───
    const kpis = useMemo(
        () => computeSprintKpis(
            sprintTasks,
            nextSprint?.startDate || null,
            nextSprint?.endDate || null,
            tasks,   // full task list for next-sprint count
        ),
        [sprintTasks, nextSprint, tasks],
    );

    const insights = useMemo(() => deriveSprintInsights(sprintTasks), [sprintTasks]);
    const completionRate = useMemo(() => computeCompletionRate(sprintTasks), [sprintTasks]);
    const carryForwardCount = useMemo(
        () => sprintTasks.filter(t => ['Backlog', 'In Progress'].includes(t.status)).length,
        [sprintTasks],
    );

    const handleCloseSprint = () => {
        if (!selectedSprint) return;
        Modal.confirm({
            title: 'Close Sprint',
            content: `Are you sure you want to close "${selectedSprint.name}"? This will archive the sprint and generate an AI Sprint Report.`,
            okText: 'Close Sprint',
            okButtonProps: { danger: true },
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await closeSprint(selectedSprint._id);
                    message.success('Sprint closed successfully. Report is being generated.');
                    refreshSelectedSprint(); // Will fetch sprints again, pulling the newly created active sprint if applicable
                } catch (err) {
                    console.error('Failed to close sprint:', err);
                    message.error('Failed to close sprint.');
                }
            },
        });
    };

    // ── Board height ──────────────────────────────────────────────────────────
    const SECTION_KEYS = TIMELINE_SECTIONS.map(s => s.key);
    const grouped = useMemo(() => groupTasksByStatus(sprintTasks, SECTION_KEYS), [sprintTasks]);
    const boardHeight = useMemo(() => {
        const total = TIMELINE_SECTIONS.reduce((acc, section) => {
            const sectionTasks = grouped.get(section.key) || [];
            const collapsed = isSectionCollapsed(section.key);
            return acc + SECTION_ROW_HEIGHT + (collapsed ? 0 : Math.max(sectionTasks.length, 1) * ROW_HEIGHT);
        }, 0);
        return Math.min(Math.max(total + SECTION_ROW_HEIGHT + 60, 300), 650);
    }, [grouped, isSectionCollapsed]);

    if (loading) {
        return <div className="flex items-center justify-center h-64 text-slate-500">Loading Sprint View...</div>;
    }

    return (
        <div className="flex flex-col gap-6 w-full animate-fade-in">

            {/* Header — real sprint data */}
            <SprintHeader
                sprints={sprints}
                sprintsLoading={sprintsLoading}
                selectedSprint={selectedSprint}
                onSprintChange={setSelectedSprint}
                onSettingsClick={() => setIsSettingsOpen(true)}
                onCloseSprint={handleCloseSprint}
            />

            {/* KPI Cards — real computed values */}
            <div data-tour="sprint-kpi-cards">
                <SprintKpiCards kpis={kpis} />
            </div>

            {/* Sprint Workspace */}
            <div className="flex flex-col lg:flex-row gap-6 overflow-x-hidden" style={{ height: boardHeight }}>
                <div data-tour="sprint-timeline" className="flex-1 min-w-0">
                    <SprintTimeline
                        tasks={sprintTasks}
                        onTaskClick={onTaskClick}
                        toggleSection={toggleSection}
                        isSectionCollapsed={isSectionCollapsed}
                    />
                </div>
                <div data-tour="sprint-insights-panel" className="w-full lg:w-96 flex-shrink-0">
                    <SprintAIInsights insights={insights} />
                </div>
            </div>

            {/* Progress Report Banner */}
            <div data-tour="sprint-progress-report">
                <SprintProgressReport
                    completionRate={completionRate}
                    carryForwardCount={carryForwardCount}
                    sprintName={selectedSprint?.name || null}
                    onGenerateReport={() => setIsReportOpen(true)}
                />
            </div>

            {/* Settings Modal — saves to backend, calls refreshSelectedSprint on success */}
            <SprintSettingsModal
                open={isSettingsOpen}
                onClose={() => setIsSettingsOpen(false)}
                selectedSprint={selectedSprint}
                onSaved={refreshSelectedSprint}
            />

            {/* Report Modal — passes sprint-filtered tasks + the selected sprint */}
            <SprintReportModal
                open={isReportOpen}
                onClose={() => setIsReportOpen(false)}
                tasks={sprintTasks}
                selectedSprint={selectedSprint}
            />
        </div>
    );
}
